import * as Sentry from '@sentry/react'
import { scrubUuids } from './utils/scrubUuids'

function getSentryEnvironment(): string {
  const baseUrl = import.meta.env.MEDPLUM_BASE_URL || ''
  if (baseUrl.includes('localhost')) return 'development'
  if (baseUrl.includes('staging')) return 'staging'
  return 'production'
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: getSentryEnvironment(),
  enabled: !!import.meta.env.VITE_SENTRY_DSN,

  // HIPAA: never send PII automatically
  sendDefaultPii: false,

  // Two standing rules for pages that can host a third-party clinical iframe (the
  // DoseSpot Prescribe modal on the chart and call pages). DoseSpot's embedded app
  // broadcasts a continuous rrweb DOM mirror — i.e. PHI — to the parent window, and
  // any 'message' listener on the page receives it:
  //   1. No BeHuman code may attach a window 'message' listener on those pages.
  //   2. No Sentry integration that breadcrumbs postMessage. The default browser
  //      integrations do not; keep it that way.
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Tracing
  tracesSampleRate: getSentryEnvironment() === 'production' ? 0.1 : 1.0,

  // Session Replay — only record sessions where an error occurs
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Strip URL query strings that may contain patient identifiers, and the uuid path
  // segments that carry them on /patients/:patientId and /call/:encounterId.
  beforeSend(event) {
    if (event.request?.query_string) {
      event.request.query_string = '[Filtered]'
    }
    if (event.request?.url) {
      event.request.url = scrubUuids(event.request.url)
    }
    return event
  },

  // Strip query params from fetch/XHR breadcrumb URLs. The path is kept for
  // diagnostics, so its uuids — FHIR reads are /fhir/R4/Patient/<uuid> — are scrubbed
  // for the same reason as beforeSend's.
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
      if (breadcrumb.data?.url) {
        try {
          const url = new URL(breadcrumb.data.url, window.location.origin)
          breadcrumb.data.url = scrubUuids(url.origin + url.pathname)
        } catch {
          // leave as-is if URL parsing fails
        }
      }
    }
    return breadcrumb
  },
})
